import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Phone, Star, Award, Users, Smile, Sparkles, Heart, Trophy, Zap, ArrowLeft, ArrowRight, User, Diamond, Sun, Ruler, Stethoscope, FileText, Tag, Menu, X, Mail, Share2, Facebook, Instagram, Twitter, MessageCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect } from 'react';
import { getAllCases } from '@/lib/firebase';
import { getAllBlogPosts } from '@/services/blogService';
import { useToast } from '@/components/ui/use-toast';
import { getWebsiteSettings, WebsiteSettings } from '@/lib/firebase';
import { useNavigate } from 'react-router-dom';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface Case {
  id: string;
  title: string;
  description: string;
  category: string;
  beforeImage: string;
  afterImage: string;
  createdAt: Date;
}

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image: string;
  author: string;
  createdAt: Date;
}

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [featuredCases, setFeaturedCases] = useState<Case[]>([]);
  const [featuredBlogPosts, setFeaturedBlogPosts] = useState<BlogPost[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsData, casesData, blogPostsData] = await Promise.all([
          getWebsiteSettings(),
          getAllCases(),
          getAllBlogPosts()
        ]);

        if (!settingsData.data) {
          throw new Error('Failed to load website settings');
        }

        setSettings(settingsData.data as WebsiteSettings);
        
        // Transform cases data to match Case interface
        const transformedCases = (casesData || []).map((case_: any) => ({
          id: case_.id || '',
          title: case_.title || '',
          description: case_.description || '',
          category: case_.category || '',
          beforeImage: case_.beforeImage || '',
          afterImage: case_.afterImage || '',
          createdAt: case_.createdAt ? new Date(case_.createdAt) : new Date()
        }));
        setFeaturedCases(transformedCases as Case[]);

        // Transform blog posts data to match BlogPost interface
        const transformedBlogPosts = (blogPostsData || []).map((post: any) => ({
          id: post.id || '',
          title: post.title || '',
          content: post.content || '',
          excerpt: post.excerpt || '',
          image: post.image || '',
          author: post.author || '',
          createdAt: post.createdAt ? new Date(post.createdAt) : new Date()
        }));
        setFeaturedBlogPosts(transformedBlogPosts as BlogPost[]);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          variant: 'destructive',
          title: 'خطأ في تحميل البيانات',
          description: 'حدث خطأ أثناء تحميل البيانات، يرجى المحاولة مرة أخرى'
        });
        setFeaturedCases([]);
        setFeaturedBlogPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [toast]);

  useEffect(() => {
    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href') || '');
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }, []);

  const getRandomGradient = () => {
    const gradients = [
      'from-blue-500 to-cyan-500',
      'from-purple-500 to-pink-500',
      'from-yellow-400 to-orange-500',
      'from-green-500 to-emerald-500',
      'from-red-500 to-rose-500'
    ];
    return gradients[Math.floor(Math.random() * gradients.length)];
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      message: formData.get('message') as string,
    };

    try {
      // Here you would typically send the data to your backend
      console.log('Form submitted:', data);
      toast({
        title: 'تم إرسال الرسالة بنجاح',
        description: 'سنقوم بالرد عليك في أقرب وقت ممكن',
      });
      e.currentTarget.reset();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: 'destructive',
        title: 'خطأ في إرسال الرسالة',
        description: 'حدث خطأ أثناء إرسال الرسالة، يرجى المحاولة مرة أخرى',
      });
    }
  };

  const handleImageChange = (direction: 'next' | 'prev') => {
    if (!settings?.clinic.images) return;
    
    setCurrentImageIndex((prevIndex) => {
      if (direction === 'next') {
        return (prevIndex + 1) % settings.clinic.images.length;
      } else {
        return (prevIndex - 1 + settings.clinic.images.length) % settings.clinic.images.length;
      }
    });
  };

  // Helper to parse Firestore Timestamp, string, or number
  function formatBlogDate(dateValue: any) {
    let date;
    if (dateValue && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      date = new Date();
    }
    if (!(date instanceof Date) || isNaN(date)) return '';
    return date.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600">لم نتمكن من تحميل بيانات الموقع، يرجى المحاولة مرة أخرى</p>
        </div>
      </div>
    );
  }

  const services = [
    {
      title: 'كشف أولي',
      description: 'فحص شامل للأسنان وتشخيص دقيق للحالة',
      icon: <Stethoscope className="w-6 h-6 text-primary" />
    },
    {
      title: 'تنظيف الأسنان',
      description: 'تنظيف احترافي وإزالة الجير والتصبغات',
      icon: <Sparkles className="w-6 h-6 text-primary" />
    },
    {
      title: 'حشو الأسنان',
      description: 'حشوات تجميلية متطورة بأحدث التقنيات',
      icon: <Star className="w-6 h-6 text-primary" />
    },
    {
      title: 'علاج العصب',
      description: 'علاج جذور الأسنان بأحدث الأجهزة',
      icon: <Stethoscope className="w-6 h-6 text-primary" />
    },
    {
      title: 'تقويم الأسنان',
      description: 'تقويم متحرك وثابت بأحدث التقنيات',
      icon: <Sparkles className="w-6 h-6 text-primary" />
    },
    {
      title: 'تبييض الأسنان',
      description: 'تبييض احترافي للحصول على ابتسامة مشرقة',
      icon: <Heart className="w-6 h-6 text-primary" />
    },
    {
      title: "خدمات طب الأسنان",
      description: "نقدم مجموعة شاملة من خدمات طب الأسنان المتخصصة",
      icon: <Stethoscope className="w-6 h-6 text-primary" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50/50">
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-extrabold mb-8 pb-2 bg-gradient-to-r from-blue-600 via-purple-500 to-green-500 bg-clip-text text-transparent animate-rotate-gradient drop-shadow-xl leading-[1.35]">
                ابتسامة صحية وجميلة تليق بك
              </h2>
              <p className="text-xl text-gray-700 mb-10 font-medium animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                {settings?.clinic?.name} — نقدم لك أفضل خدمات طب الأسنان بأيدي خبراء متخصصين
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 rounded-full font-semibold shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  <Sparkles className="ml-2 w-5 h-5" />
                  احجز موعدك الآن
                </Button>
              </Link>
              <a href="#services">
                <Button size="lg" variant="outline" className="px-8 py-6 rounded-full font-semibold hover:bg-primary hover:text-white transition-all duration-300">
                  خدماتنا
                  <ArrowRight className="mr-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Doctor Image */}
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all duration-500">
                  <img
                    src={settings?.doctor?.image || '/doctor-placeholder.jpg'}
                    alt={settings?.doctor?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-2xl shadow-xl">
                  <div className="flex items-center gap-2">
                    <Award className="w-6 h-6 text-yellow-500" />
                    <span className="text-sm font-semibold text-gray-700">
                      خبرة {settings?.doctor?.statistics?.experienceYears || 0} سنوات
                    </span>
                  </div>
                </div>
              </div>

              {/* Doctor Info */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {settings?.doctor?.name}
                  </h2>
                  <p className="text-xl text-gray-600">{settings?.doctor?.title}</p>
                </div>

                <div className="prose prose-lg text-gray-600">
                  <p>{settings?.doctor?.about}</p>
                </div>

                {/* Qualifications */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-800">المؤهلات العلمية</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {settings?.doctor?.qualifications?.filter(q => q.trim()).map((qualification, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="bg-primary/10 p-2 rounded-lg">
                          <Trophy className="w-5 h-5 text-primary" />
                        </div>
                        <p className="text-gray-700">{qualification}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 pt-6">
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-2xl font-bold text-primary">
                      {settings?.doctor?.statistics?.experienceYears || 0} سنوات
                    </div>
                    <p className="text-sm text-gray-600">سنوات الخبرة</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-2xl font-bold text-primary">
                      +{settings?.doctor?.statistics?.successfulCases || 0}
                    </div>
                    <p className="text-sm text-gray-600">حالة ناجحة</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                    <div className="text-2xl font-bold text-primary">
                      {settings?.doctor?.statistics?.satisfactionRate || 0}%
                    </div>
                    <p className="text-sm text-gray-600">رضا المرضى</p>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="pt-6">
                  <Link to="/booking">
                    <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 rounded-xl text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      <Sparkles className="ml-2 w-5 h-5" />
                      احجز موعدك الآن
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            خدماتنا
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              أحدث الحالات
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              نقدم لكم مجموعة من أحدث الحالات التي تم علاجها في عيادتنا، مع صور قبل وبعد العلاج
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {featuredCases.map((case_, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02] bg-white/50 backdrop-blur-sm border-0">
                <CardContent className="p-6">
                  {/* Category Badge */}
                  <div className="mb-4 flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-2 rounded-full w-fit">
                    <Tag className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium text-primary">{case_.category}</span>
                  </div>

                  {/* Images Container */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Before Image */}
                    <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={case_.beforeImage}
                        alt="قبل العلاج"
                        className="w-full h-full object-contain bg-gray-50/50"
                      />
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-primary text-sm px-3 py-1.5 rounded-full font-medium shadow-sm">
                        قبل
                      </div>
                    </div>

                    {/* After Image */}
                    <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                      <img
                        src={case_.afterImage}
                        alt="بعد العلاج"
                        className="w-full h-full object-contain bg-gray-50/50"
                      />
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-primary text-sm px-3 py-1.5 rounded-full font-medium shadow-sm">
                        بعد
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" className="group">
              <Link to="/cases" className="flex items-center gap-2">
                عرض جميع الحالات
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="relative py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20 animate-fade-in">
            <div className="relative inline-block mb-8">
              <h2 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent mb-6">أحدث المقالات</h2>
              <FileText className="absolute -top-4 -right-4 w-8 h-8 text-blue-500 animate-float" />
            </div>
            <p className="text-2xl text-gray-600">تعرف على أحدث المقالات والنصائح الطبية</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredBlogPosts.map((post, index) => (
              <Card key={index} className="hover-scale bg-white/90 backdrop-blur-xl border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 rounded-3xl overflow-hidden group animate-fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <CardHeader className="p-6">
                  <CardTitle className="text-xl font-bold text-gray-800 line-clamp-2">{post.title}</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatBlogDate(post.createdAt)}</span>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-2 px-4 rounded-xl transition-all duration-300 transform hover:scale-105"
                    onClick={() => navigate(`/blog/${post.id}`)}
                  >
                    قراءة المزيد
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Working Hours Bar */}
      {settings?.clinic?.workingHours && settings.clinic.workingHours.length > 0 && (
        <div className="bg-primary/5 border-b">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
              {settings.clinic.workingHours.map((hours, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>{hours}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              تواصل معنا
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              نحن هنا لمساعدتك في الحصول على أفضل رعاية صحية لأسنانك
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-6">
              {settings?.clinic?.phone && settings.clinic.phone.length > 0 && (
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">أرقام الهاتف</h3>
                    <div className="space-y-2">
                      {settings.clinic.phone.map((phone, index) => (
                        <a 
                          key={index}
                          href={`tel:${phone}`}
                          className="block text-muted-foreground hover:text-primary transition-colors"
                        >
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {settings?.clinic?.address && settings.clinic.address.length > 0 && (
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">العنوان</h3>
                    <div className="space-y-1 text-muted-foreground">
                      {settings.clinic.address.map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {settings?.clinic?.workingHours && settings.clinic.workingHours.length > 0 && (
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">ساعات العمل</h3>
                    <div className="space-y-1 text-muted-foreground">
                      {settings.clinic.workingHours.map((hours, index) => (
                        <p key={index}>{hours}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Social Media */}
              {(settings?.clinic?.socialMedia?.facebook || 
                settings?.clinic?.socialMedia?.instagram || 
                settings?.clinic?.socialMedia?.twitter || 
                settings?.clinic?.socialMedia?.whatsapp) && (
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <MessageCircle className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">تابعنا على وسائل التواصل الاجتماعي</h3>
                    <div className="flex gap-4">
                      {settings.clinic.socialMedia.facebook && (
                        <a 
                          href={settings.clinic.socialMedia.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full bg-blue-100 hover:bg-blue-200 p-2 transition-colors shadow"
                        >
                          <Facebook className="w-6 h-6 text-blue-600" />
                        </a>
                      )}
                      {settings.clinic.socialMedia.instagram && (
                        <a 
                          href={settings.clinic.socialMedia.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full bg-pink-100 hover:bg-pink-200 p-2 transition-colors shadow"
                        >
                          <Instagram className="w-6 h-6 text-pink-500" />
                        </a>
                      )}
                      {settings.clinic.socialMedia.twitter && (
                        <a 
                          href={settings.clinic.socialMedia.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full bg-sky-100 hover:bg-sky-200 p-2 transition-colors shadow"
                        >
                          <Twitter className="w-6 h-6 text-sky-500" />
                        </a>
                      )}
                      {settings.clinic.socialMedia.whatsapp && (
                        <a 
                          href={`https://wa.me/${settings.clinic.socialMedia.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full bg-green-100 hover:bg-green-200 p-2 transition-colors shadow"
                        >
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Google Maps */}
            {settings?.clinic?.contactInfo?.mapEmbed && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div 
                  className="w-full h-full min-h-[300px] rounded-xl overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: settings.clinic.contactInfo.mapEmbed }}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {settings?.qualifications?.map((qualification, index) => (
              <Card key={index} className="hover-scale bg-white/80 backdrop-blur-sm border border-gray-100 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900">{qualification.title}</CardTitle>
                  <CardDescription className="text-gray-600">{qualification.institution}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{qualification.description}</p>
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{qualification.year}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* معرض العيادة */}
      {settings?.clinic?.images && settings.clinic.images.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              معرض العيادة
            </h2>
            <div className="relative max-w-4xl mx-auto">
              <div className="relative h-[400px] overflow-hidden rounded-2xl shadow-2xl">
                {settings.clinic.images.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      currentImageIndex === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`صورة العيادة ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              
              {/* Navigation Buttons */}
              <button
                onClick={() => handleImageChange('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              <button
                onClick={() => handleImageChange('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>

              {/* Dots Navigation */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {settings.clinic.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      currentImageIndex === index ? 'bg-white scale-125' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;

